import { MapsContext, Utils } from '@platform/maplibre';
import { PeTypes, ProjectTypes } from '@platform/types';
import { Helpers } from '@platform/utils';
import { LngLatBounds, Map } from 'maplibre-gl';
import React from 'react';
import { RuleGroupType, transformQuery } from 'react-querybuilder';
import { v4 as uuidv4 } from 'uuid';
import { useSessionContext } from '../../contexts/SessionContext';
import { useDatasetColumns, useDatasetLastPublishedState } from '../../hooks';
import * as peApi from '../../pe.api';
import SeProMap from '../Map/SeProMap';

const { getWorkingLayerFingerprint } = Utils;

interface Props {
  dataset: PeTypes.Dataset;
  filter: RuleGroupType;
}

const DatasetTiles: React.FC<Props> = ({ dataset, filter }) => {
  const { jwtToken } = useSessionContext();
  const datasetColumnQuery = useDatasetColumns({ datasetId: dataset.id, itemsPerPage: dataset.numberOfColumns });
  const { query: datasetLastPublishedStateQuery } = useDatasetLastPublishedState({ datasetId: dataset.id });

  if (datasetColumnQuery.isLoading || datasetColumnQuery.isIdle) {
    return <>Loading...</>;
  }

  if (datasetColumnQuery.isError) {
    return <>Something went wrong</>;
  }

  const { projections = [] } = dataset.tiles;
  const [projection] = projections;
  const { xmin, xmax, ymin, ymax } = projection.extent;

  const bounds = new LngLatBounds(
    [xmax, ymin], // southwest coordinates
    [xmin, ymax] // northeast coordinates
  );

  const workingLayer: ProjectTypes.UserUploadedWorkingLayer = {
    id: uuidv4(),
    source: 'user-upload',
    name: dataset.name,
    description: '',
    variableSelection: [],
    maskingSelection: { doMask: false, features: [] },
    featureSelection: [],
    metadata: {
      datasetId: dataset.id,
      tiles: `${process.env.NX_USER_TILES_SERVER}?x={x}&y={y}&z={z}&layers={layers}&columns={columns}&projection=EPSG-3857`,
      geoLayerId: dataset.tiles.geoLayerId?.toString() ?? '',
      dataLayerId: '0',
      identifiers: {
        row: {
          variableId: 'ID',
          datasetId: '0',
        },
      },
    },
    rendering: {
      type: 'simple',
      style: {
        baseColor: '#4F46E5',
        opacities: [0.82, 0.82],
      },
    },
    tooltip: {
      enabled: datasetLastPublishedStateQuery.data?.columns != null, // TODO: enable
      content:
        datasetLastPublishedStateQuery.data?.columns.map((col) => ({
          variableId: col.name,
          id: col.id,
          label: col.label ?? col.name,
          format: col.format,
        })) ?? [],
      action: 'click',
      title: undefined,
    },
    filter: transformQuery(
      {
        ...filter,
        rules: filter.rules.filter((rule) =>
          Helpers.checkIfValidRule(
            rule,
            datasetColumnQuery.data.map((col) => col.uuid)
          )
        ),
      },
      {
        ruleProcessor: (r) => ({
          ...r,
          field: datasetColumnQuery.data.find((col) => col.uuid === r.field)?.title, //column uuid must be turned into column title in order to get tiles
        }),
      }
    ),

    visible: true,
    type: dataset.type,
  };

  const fingerPrint = getWorkingLayerFingerprint(workingLayer);

  return (
    <div className="m-2 flex h-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white">
      <MapsContext.MapsContextProvider
        mapContextId={fingerPrint}
        jwtToken={jwtToken}
        doSave={false}
        apis={{
          baseMaps: { get: peApi.getBaseMapStyle },
          mapContext: {
            get: () =>
              Promise.resolve({
                id: uuidv4(),
                type: 'single',
                mapObjects: [
                  {
                    id: uuidv4(),
                    styleSpec: {
                      baseMapId: 'streets-v2',
                      workingLayers: [workingLayer],
                      center: [-97.31211029941215, 44.16617302763339],
                      zoom: 2,
                    },
                    order: 1,
                  },
                ],
              }),
            update: () => new Promise((s) => ({})),
          },
        }}
      >
        <MapsContext.MapsConsumer>
          {({ state }) => {
            if (!state.registeredMaps.length) return null;
            const [mapObj] = state.registeredMaps;
            return (
              <SeProMap
                mapObj={mapObj}
                mode="view"
                onMapMount={(mapRef: Map) => mapRef?.fitBounds(bounds, { padding: 100, animate: true })}
              />
            );
          }}
        </MapsContext.MapsConsumer>
      </MapsContext.MapsContextProvider>
    </div>
  );
};

export default DatasetTiles;
